export type LegalAgreementPermissions = {
  agreed_to_terms_condition?: boolean;
  agreed_to_privacy_policy?: boolean;
};

export type LegalAgreementAcceptanceResponse = LegalAgreementPermissions & {
  agreed_to_terms_condition_at?: string | null;
  agreed_to_privacy_policy_at?: string | null;
};

export function mustAcceptLegalAgreements(
  permissions: LegalAgreementPermissions | null | undefined,
): boolean {
  if (!permissions) return false;
  return (
    !permissions.agreed_to_terms_condition ||
    !permissions.agreed_to_privacy_policy
  );
}

export function hasLegacyLocalStorageAcceptance(): boolean {
  try {
    const raw = localStorage.getItem("customSettings");
    if (!raw) return false;
    return JSON.parse(raw).agreed_tc_v2025_02 === true;
  } catch {
    return false;
  }
}

export async function syncLegacyAcceptanceIfNeeded<T extends LegalAgreementPermissions>(
  permissions: T,
  acceptLegalAgreements: () => Promise<{ data: LegalAgreementAcceptanceResponse }>,
  refreshPermissions: () => Promise<void>,
): Promise<void> {
  if (!hasLegacyLocalStorageAcceptance()) return;
  if (!mustAcceptLegalAgreements(permissions)) return;

  const mergeAcceptance = (acceptance: LegalAgreementAcceptanceResponse) => {
    Object.assign(permissions, applyLegalAgreementAcceptanceToPermissions(permissions, acceptance));
  };

  try {
    const response = await acceptLegalAgreements();
    await refreshPermissions();
    // Apply after refresh: GET /user/permissions may still return a cached payload
    // with agreed_to_* false even though POST /user/legal-agreements succeeded.
    mergeAcceptance(response.data);
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 400) {
      mergeAcceptance({
        agreed_to_terms_condition: true,
        agreed_to_privacy_policy: true,
      });
      return;
    }
    throw err;
  }
}

export function applyLegalAgreementAcceptanceToPermissions<T extends LegalAgreementPermissions>(
  permissions: T,
  acceptance: LegalAgreementAcceptanceResponse,
): T {
  return {
    ...permissions,
    agreed_to_terms_condition: acceptance.agreed_to_terms_condition ?? true,
    agreed_to_privacy_policy: acceptance.agreed_to_privacy_policy ?? true,
  };
}
